import { type FC, type MouseEventHandler, type ReactNode } from 'react';
import { graphql, useFragment } from 'react-relay';
import { Link } from 'dibs-elements/exports/Link';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel';
import classnames from 'classnames';

import { type PhotoHeading_photo$key } from './__generated__/PhotoHeading_photo.graphql';
import styles from './PhotoHeading.scss';

interface Props {
    handleLinkClick?: MouseEventHandler<Element>;
    linkHref?: string;
    className?: string;
    linkContent?: ReactNode;
    hasStyleFacet?: boolean;
    hasRoomFacet?: boolean;
    hasProjectTypeFacet?: boolean;
    photo: PhotoHeading_photo$key;
    selectedStyles?: string[];
}

const PhotoHeading: FC<Props> = ({
    handleLinkClick = () => {},
    linkHref,
    className,
    linkContent,
    hasStyleFacet,
    hasRoomFacet,
    selectedStyles = [],
    hasProjectTypeFacet,
    photo: photoRef,
}) => {
    const photo = useFragment(
        graphql`
            fragment PhotoHeading_photo on PhotosSearchBrowsePhoto {
                styles {
                    name
                    urlLabel
                }
                room {
                    name
                }
                project {
                    projectTypes
                }
            }
        `,
        photoRef
    );
    let headingCopy: string[] = [];

    let style = photo?.styles?.[0]?.name || '';
    const roomType = photo?.room?.name || '';
    const projectType = photo?.project?.projectTypes?.[0] || '';

    if (hasStyleFacet) {
        const currentStyleFacetName = (photo?.styles || []).find(
            facet => facet?.urlLabel === selectedStyles[0]
        )?.name;
        style = currentStyleFacetName || style;
    }

    switch (true) {
        case hasRoomFacet && hasProjectTypeFacet:
        case hasStyleFacet && hasProjectTypeFacet:
        case hasStyleFacet && hasRoomFacet:
            headingCopy = [style, projectType, roomType];
            break;
        case hasProjectTypeFacet:
            headingCopy = [style, projectType];
            break;
        case hasRoomFacet:
        case hasStyleFacet:
        default:
            headingCopy = [style, roomType];
    }
    return (
        <HeadingLevel>
            {Heading => (
                <div className={classnames(styles.headingContainer, className)}>
                    <Heading className={styles.heading}>
                        <Link
                            className={styles.headingLink}
                            onClick={handleLinkClick}
                            href={linkHref}
                        >
                            {headingCopy.join(' ')}
                        </Link>
                        {linkContent}
                    </Heading>
                </div>
            )}
        </HeadingLevel>
    );
};

export default PhotoHeading;
